import React, { memo, useMemo, useState } from 'react';

import { Box, Button } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import type { EmaCurationListItem } from '../../../types/dtos/ema';
import Styles from './EMACurations.styles';
import type { EmaCurationSubflow } from './EMACurations.types';

const ACTIONS: Array<{ key: EmaCurationSubflow; label: string }> = [
  { key: 'procedural_steps', label: 'Procedural Steps' },
  { key: 'label_separation', label: 'Label Separation' },
  { key: 'products', label: 'Products' }
];

const formatArrivedAt = (value: string | null): string => {
  if (!value) {
    return 'N/A';
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

/* eslint-disable no-unused-vars -- callback parameter names document the typed props */
type EmaCurationsTableProps = {
  rows: EmaCurationListItem[];
  loading: boolean;
  actionsEnabled: boolean;
  onNavigate(row: EmaCurationListItem, subflow: EmaCurationSubflow): void;
};
/* eslint-enable no-unused-vars */

const EmaCurationsTable = ({
  rows,
  loading,
  actionsEnabled,
  onNavigate
}: EmaCurationsTableProps) => {
  const classes = Styles();
  const [pageSize, setPageSize] = useState(10);
  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'curation_id',
        headerName: 'Curation ID',
        minWidth: 280,
        flex: 1
      },
      {
        field: 'product_number',
        headerName: 'Product Number',
        minWidth: 210,
        flex: 0.8
      },
      {
        field: 'arrived_at',
        headerName: 'Arrived At',
        minWidth: 190,
        flex: 0.7,
        renderCell: params => formatArrivedAt(params.value as string | null)
      },
      {
        field: 'available_curations',
        headerName: 'Available Curations',
        minWidth: 520,
        flex: 1.8,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as EmaCurationListItem;

          return (
            <Box className={classes.actions}>
              {ACTIONS.map(action => {
                const enabled = actionsEnabled && row.available_curations[action.key];

                return (
                  <Button
                    key={action.key}
                    className={classes.actionButton}
                    variant='contained'
                    size='small'
                    disabled={!enabled}
                    onClick={() => onNavigate(row, action.key)}>
                    {action.label}
                  </Button>
                );
              })}
            </Box>
          );
        }
      }
    ],
    [actionsEnabled, classes.actionButton, classes.actions, onNavigate]
  );

  return (
    <Box className={classes.table}>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={row => row.curation_id}
        loading={loading}
        disableColumnMenu
        disableSelectionOnClick
        autoHeight
        pagination
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        rowsPerPageOptions={[10, 20, 50]}
        getRowHeight={() => 'auto'}
      />
    </Box>
  );
};

export default memo(EmaCurationsTable);
